import { useStudio } from '../../studios/StudioContext';
import { useState, useEffect } from "react";
import { useSession, useLoginForm, logout, changePassword } from "../../auth/session";
import BookingsStudentsTab from "./tabs/BookingsStudentsTab";
import CalendarTab  from "./tabs/CalendarTab";
import ReportsTab   from "./tabs/ReportsTab";
import CheckInTab   from "./tabs/CheckInTab";
import SettingsTab  from "./tabs/SettingsTab";
import MerchTab from "./tabs/MerchTab";
import MessagesTab from "./tabs/MessagesTab";
import NewslettersTab from "./tabs/NewslettersTab";
import VideosTab from "./tabs/VideosTab";
import "./admin-base.css";
import "./admin-layout.css";
import "./admin-controls.css";
import "./admin-students.css";
import "./admin-misc.css";
import "./admin-responsive.css";

const TABS = [
  { id: "bookings",    label: "Bookings & Students", Component: BookingsStudentsTab },
  { id: "calendar",    label: "Calendar",            Component: CalendarTab,    ownerOnly: true },
  { id: "reports",     label: "Reports",             Component: ReportsTab,     ownerOnly: true },
  { id: "merch",       label: "Sales & Merch",       Component: MerchTab },
  { id: "messages",    label: "Messages",            Component: MessagesTab },
  { id: "newsletters", label: "Newsletters",         Component: NewslettersTab },
  { id: "checkin",     label: "Check-in",            Component: CheckInTab },
  { id: "settings",    label: "Settings",            Component: SettingsTab,    ownerOnly: true },
  { id: "videos",      label: "Videos",              Component: VideosTab,      ownerOnly: true },
];

export function LoginGate({ api, studioName }) {
  const f = useLoginForm(api);
  return (
    <div className="adm-gate">
      <form className="adm-gate-card" onSubmit={e => { e.preventDefault(); f.submit(); }}>
        <div className="adm-gate-logo">⚙</div>
        <h1 className="adm-gate-title">Admin Dashboard</h1>
        <p className="adm-gate-sub">{studioName}</p>
        <input autoFocus type="email" name="email" autoComplete="username"
          className={`adm-gate-input ${f.error ? "input-error" : ""}`}
          placeholder="Email" value={f.email}
          onChange={e => { f.setEmail(e.target.value); f.setError(""); }} />
        <input type="password" name="password" autoComplete="current-password"
          className={`adm-gate-input ${f.error ? "input-error" : ""}`}
          placeholder="Password" value={f.password}
          onChange={e => { f.setPassword(e.target.value); f.setError(""); }} />
        {f.error && <p className="adm-gate-error">{f.error}</p>}
        <button type="submit" className="adm-btn adm-btn--primary" disabled={f.busy}>
          {f.busy ? "Signing in…" : "Log in"}
        </button>
      </form>
    </div>
  );
}

function NoAccess({ api, studioName, user }) {
  return (
    <div className="adm-gate">
      <div className="adm-gate-card">
        <div className="adm-gate-logo">⚙</div>
        <h1 className="adm-gate-title">No access</h1>
        <p className="adm-gate-sub">{user?.email} doesn't have access to {studioName}.</p>
        <button className="adm-btn adm-btn--primary" onClick={() => logout(api)}>Log out</button>
      </div>
    </div>
  );
}

function ChangePasswordModal({ api, onClose }) {
  const [form, setForm]   = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone]   = useState(false);
  const [busy, setBusy]   = useState(false);
  const set = k => e => { setForm(f => ({ ...f, [k]: e.target.value })); setError(""); };

  const submit = async e => {
    e.preventDefault();
    if (form.next.trim().length < 10) { setError("New password must be at least 10 characters."); return; }
    if (form.next !== form.confirm)   { setError("New passwords don't match."); return; }
    setBusy(true);
    try { await changePassword(api, form.current, form.next); setDone(true); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3 className="adm-modal-title">Change password</h3>
          <button className="adm-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="adm-modal-body">
          {done ? (
            <>
              <p>Password changed. Other devices have been signed out.</p>
              <div className="adm-modal-actions">
                <button className="adm-btn-primary" onClick={onClose}>Done</button>
              </div>
            </>
          ) : (
            <form onSubmit={submit}>
              <div className="adm-field-group">
                <label className="adm-label">Current password</label>
                <input className="adm-input" type="password" autoComplete="current-password" value={form.current} onChange={set("current")} required />
              </div>
              <div className="adm-field-group">
                <label className="adm-label">New password (10+ characters)</label>
                <input className="adm-input" type="password" autoComplete="new-password" value={form.next} onChange={set("next")} required />
              </div>
              <div className="adm-field-group">
                <label className="adm-label">Confirm new password</label>
                <input className="adm-input" type="password" autoComplete="new-password" value={form.confirm} onChange={set("confirm")} required />
              </div>
              {error && <p className="adm-error">{error}</p>}
              <div className="adm-modal-actions">
                <button type="button" className="adm-btn-ghost-sm" onClick={onClose}>Cancel</button>
                <button type="submit" className="adm-btn-primary" disabled={busy}>{busy ? "Saving…" : "Change password"}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const studio                  = useStudio();
  const api                     = studio?.apiBaseUrl;
  const session                 = useSession(api);
  const token                   = session?.token;
  const [tab, setTab]           = useState("bookings");
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPw, setShowPw]     = useState(false);

  // Drop a stored token the server no longer accepts (expired, revoked, deactivated).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(`${api}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!cancelled && r.status === 401) logout(api); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api, token]);

  if (!session) return <LoginGate api={api} studioName={studio?.studioName} />;
  if (!session.user?.studios?.includes(studio?.studioId)) {
    return <NoAccess api={api} studioName={studio?.studioName} user={session.user} />;
  }

  const isOwner     = session.user.role === "owner";
  const visibleTabs = TABS.filter(t => isOwner || !t.ownerOnly);
  const current     = visibleTabs.find(t => t.id === tab) ?? visibleTabs[0];
  const Active  = current.Component;

  return (
    <div className="adm-wrap">
      <div className="adm-header">
        <div className="adm-header-left">
          <span className="adm-header-logo">⚙</span>
          <div>
            <h1 className="adm-header-title">Admin Dashboard</h1>
            <p className="adm-header-sub">{studio?.studioName || "Florium Demo Studio"}</p>
          </div>
        </div>
        <div className="adm-header-right">
          <a href={studio?.siteUrl || `/${studio?.studioId}`} className="adm-back-link">← Back to site</a>
          {" · "}
          <a href="#password" className="adm-back-link" onClick={e => { e.preventDefault(); setShowPw(true); }}>Password</a>
          {" · "}
          <a href="#logout" className="adm-back-link" title={session.user.email}
             onClick={e => { e.preventDefault(); logout(api); }}>Log out</a>
        </div>
      </div>

      <div className="adm-tabs">
        <button className="adm-hamburger" onClick={() => setMenuOpen(o => !o)}>
          <span /><span /><span />
        </button>
        <span className="adm-tab-current">{current.label}</span>
        <nav className={`adm-tab-menu ${menuOpen ? "open" : ""}`}>
          {visibleTabs.map(t => (
            <button key={t.id}
              className={`adm-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => { setTab(t.id); setMenuOpen(false); }}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>
      <Active />
      {showPw && <ChangePasswordModal api={api} onClose={() => setShowPw(false)} />}
    </div>
  );
}
